import { useEffect, useState, ReactNode, useRef } from 'react';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { getCachedUser } from '../../utils/userCache';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import socketService from '@/services/socket';
import api from '@/services/api';
import { useToast } from '@/components/common/ToastProvider';
import { startRegistration } from '@simplewebauthn/browser';
import { useAudioDevices } from '@/hooks/useAudioDevices';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useVoicePreferences } from '@/hooks/useVoicePreferences';
import SoundSettings from '@/components/settings/SoundSettings';

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PasskeyCredential {
  id: string;
  deviceName: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

interface NotificationConfig {
  id: string;
  notifyOnMessage: boolean;
  notifyOnMention: boolean;
  notifyOnFriendJoin: boolean;
  notifyOnVoiceJoin: boolean;
  notifyOnSpecificUsers: string | null;
}

// Small wrapper to keep sections consistent and reduce visual clutter.
function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-brand-surface dark:bg-[#252350] border border-brand-outline-variant/60 dark:border-[#3730a3] rounded-2xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff]">
            {title}
          </div>
          {description && (
            <div className="text-xs text-brand-outline dark:text-[#c4b5fd] mt-1">
              {description}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function ToggleSwitch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-3 cursor-pointer group select-none">
      <div className="relative w-12 h-7 flex-shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
        />
        <div
          className={`w-12 h-7 rounded-full transition-all duration-200 ${
            checked
              ? 'bg-amber-500 shadow-md shadow-amber-500/30'
              : 'bg-white/15 border border-white/20 group-hover:bg-white/20'
          }`}
        />
        <div
          className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </div>
      <span className="text-sm font-semibold text-brand-on-surface dark:text-[#e0e7ff] group-hover:text-white transition-colors">
        {label}
      </span>
    </label>
  );
}

export default function UserSettingsModal({ isOpen, onClose }: UserSettingsModalProps) {
  const user = useCurrentUser();
  const [activeTab, setActiveTab] = useState<
    'profile' | 'voice' | 'sounds' | 'security' | 'notifications'
  >('profile');
  const [status, setStatus] = useState<string>(user.status || 'online');
  const [twoFAEnabled, setTwoFAEnabled] = useState<boolean>(!!user.twoFactorEnabled);
  const [twoFASecret, setTwoFASecret] = useState<string>('');
  const [twoFAQr, setTwoFAQr] = useState<string>('');
  const [twoFAToken, setTwoFAToken] = useState<string>('');
  const [setupInProgress, setSetupInProgress] = useState<boolean>(false);
  const [avatarUploading, setAvatarUploading] = useState<boolean>(false);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [newUsername, setNewUsername] = useState<string>('');
  const [usernamePassword, setUsernamePassword] = useState<string>('');
  const [emailInput, setEmailInput] = useState<string>(user.email || '');
  const [emailPassword, setEmailPassword] = useState<string>('');
  const [bioInput, setBioInput] = useState<string>(user.bio || '');
  const [bioSaving, setBioSaving] = useState<boolean>(false);
  const [passkeys, setPasskeys] = useState<PasskeyCredential[]>([]);
  const [passkeyName, setPasskeyName] = useState('');
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [deletePassword, setDeletePassword] = useState<string>('');
  const [deleteConfirmation, setDeleteConfirmation] = useState<string>('');
  const [globalNotifConfig, setGlobalNotifConfig] = useState<NotificationConfig | null>(null);
  const [voiceActivityLevel, setVoiceActivityLevel] = useState(0);
  const [isMonitoringMic, setIsMonitoringMic] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const monitoringRafRef = useRef<number | null>(null);
  const toast = useToast();
  const navigate = useNavigate();

  const modalRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalRef, onClose);

  // Voice and audio device hooks
  const {
    inputDevices,
    outputDevices,
    selectedInputId,
    selectedOutputId,
    setInputDevice,
    setOutputDevice,
    refreshDevices,
    requestPermission,
    permissionGranted,
  } = useAudioDevices();

  const {
    preferences: voicePreferences,
    loading: voiceLoading,
    updatePreferences: updateVoicePreferences,
  } = useVoicePreferences();

  // Sync with auto-detected status
  useEffect(() => {
    const interval = setInterval(() => {
      const user = getCachedUser();
      if (user.status && user.status !== status) {
        setStatus(user.status);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [status]);

  useEffect(() => {
    if (!isOpen) return;
    let refreshTimer: NodeJS.Timeout;
    // Load 2FA status when modal opens
    api
      .get('/2fa/status')
      .then((res) => setTwoFAEnabled(res.data.enabled))
      .catch(() => {});
    // Load passkeys when modal opens
    fetchPasskeys();
    // Load global notification settings
    api.get('/notifications/global').then((res) => setGlobalNotifConfig(res.data.config));
    // Sign avatar URL for preview
    const refreshPreview = () => {
      const raw = user.avatarUrl;
      if (raw) {
        const filename = raw.split('/').pop();
        api
          .get('/uploads/sign', { params: { filename } })
          .then((res) => {
            setAvatarPreview(res.data.url);
            // Refresh 30s before expiry
            const expiresIn = (res.data.exp - Math.floor(Date.now() / 1000)) * 1000;
            if (expiresIn > 30000) {
              refreshTimer = setTimeout(refreshPreview, expiresIn - 30000);
            }
          })
          .catch(() => setAvatarPreview(raw));
      }
    };
    refreshPreview();

    const handleNotificationsUpdate = () => {
      api
        .get('/notifications/global')
        .then((res) => setGlobalNotifConfig(res.data.config))
        .catch(() => {});
    };

    socketService.on('user:notifications-updated', handleNotificationsUpdate);

    return () => {
      clearTimeout(refreshTimer);
      socketService.off('user:notifications-updated', handleNotificationsUpdate);
    };
  }, [isOpen]);

  // Monitor voice activity when voice tab is open AND user enables test
  useEffect(() => {
    if (activeTab !== 'voice' || !isOpen || !isMonitoringMic) {
      // Clean up monitoring when tab is closed or test is stopped
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (monitoringRafRef.current) {
        cancelAnimationFrame(monitoringRafRef.current);
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;
      }
      setVoiceActivityLevel(0);
      return;
    }

    const startVoiceActivityMonitoring = async () => {
      try {
        // Request microphone permission first
        if (typeof requestPermission === 'function') {
          await requestPermission();
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: selectedInputId ? { exact: selectedInputId } : undefined,
          },
        });
        micStreamRef.current = stream;

        const audioContext = audioContextRef.current ?? new AudioContext();
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 512;
        analyserRef.current = analyser;
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const monitor = () => {
          analyser.getByteFrequencyData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i];
          }
          const avg = sum / dataArray.length;
          setVoiceActivityLevel(Math.max(avg, 0)); // Ensure non-negative value
          monitoringRafRef.current = requestAnimationFrame(monitor);
        };

        monitoringRafRef.current = requestAnimationFrame(monitor);
      } catch (err) {
        console.error('Failed to access microphone for monitoring:', err);
        setIsMonitoringMic(false);
      }
    };

    startVoiceActivityMonitoring();

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (monitoringRafRef.current) {
        cancelAnimationFrame(monitoringRafRef.current);
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;
      }
      setVoiceActivityLevel(0);
    };
  }, [activeTab, isOpen, selectedInputId, isMonitoringMic]);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setAvatarUploading(true);
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/uploads/avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const rawUrl = res.data.avatarUrl;
      const updated = { ...user, avatarUrl: rawUrl };
      localStorage.setItem('user', JSON.stringify(updated));
      window.dispatchEvent(new Event('user-updated'));
      // Get signed preview
      const filename = rawUrl.split('/').pop();
      try {
        const signRes = await api.get('/uploads/sign', { params: { filename } });
        setAvatarPreview(signRes.data.url);
      } catch {}
      toast.success('Avatar updated');
    } catch (err) {
      console.error('Avatar upload failed:', err);
      toast.error('Avatar upload failed');
    } finally {
      setAvatarUploading(false);
    }
  };

  const startTwoFASetup = async () => {
    try {
      setSetupInProgress(true);
      const res = await api.post('/2fa/setup');
      setTwoFASecret(res.data.secret);
      setTwoFAQr(res.data.qrCode);
    } catch (err) {
      console.error('2FA setup failed:', err);
      setSetupInProgress(false);
    }
  };

  const verifyTwoFA = async () => {
    try {
      const res = await api.post('/2fa/verify', { secret: twoFASecret, token: twoFAToken });
      if (res.data.enabled) {
        setTwoFAEnabled(true);
        setSetupInProgress(false);
        setTwoFASecret('');
        setTwoFAQr('');
        setTwoFAToken('');
        const updated = { ...user, twoFactorEnabled: true };
        localStorage.setItem('user', JSON.stringify(updated));
        window.dispatchEvent(new Event('user-updated'));
        toast.success('2FA enabled');
      }
    } catch (err) {
      console.error('2FA verify failed:', err);
      toast.error('Invalid 2FA code');
    }
  };

  const disableTwoFA = async () => {
    try {
      const res = await api.post('/2fa/disable', { token: twoFAToken });
      if (res.data.enabled === false) {
        setTwoFAEnabled(false);
        setTwoFAToken('');
        const updated = { ...user, twoFactorEnabled: false };
        localStorage.setItem('user', JSON.stringify(updated));
        window.dispatchEvent(new Event('user-updated'));
        toast.success('2FA disabled');
      }
    } catch (err) {
      console.error('2FA disable failed:', err);
      toast.error('Failed to disable 2FA');
    }
  };

  const handleUsernameChange = async () => {
    if (!newUsername || !usernamePassword) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const res = await api.put('/auth/username', {
        newUsername,
        password: usernamePassword,
      });
      const updated = { ...user, username: res.data.user.username };
      localStorage.setItem('user', JSON.stringify(updated));
      window.dispatchEvent(new Event('user-updated'));
      setNewUsername('');
      setUsernamePassword('');
      toast.success('Username updated successfully');
    } catch (err: any) {
      console.error('Username update failed:', err);
      toast.error(err.response?.data?.error || 'Failed to update username');
    }
  };

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      const res = await api.put('/auth/password', {
        currentPassword,
        newPassword,
      });
      if (res.data?.accessToken) {
        localStorage.setItem('accessToken', res.data.accessToken);
      }
      if (res.data?.refreshToken) {
        localStorage.setItem('refreshToken', res.data.refreshToken);
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password updated! All other active sessions have been logged out.');
    } catch (err: any) {
      console.error('Password update failed:', err);
      toast.error(err.response?.data?.error || 'Failed to update password');
    }
  };

  const handleEmailChange = async () => {
    if (!emailPassword) {
      toast.error('Please enter your password to update email');
      return;
    }

    try {
      const res = await api.put('/auth/email', {
        email: emailInput.trim() || null,
        password: emailPassword,
      });
      const updatedUser = { ...user, email: res.data.user.email };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      window.dispatchEvent(new Event('user-updated'));
      setEmailPassword('');
      toast.success('Email updated');
    } catch (err: any) {
      console.error('Email update failed:', err);
      toast.error(err.response?.data?.error || 'Failed to update email');
    }
  };

  const handleUpdateBio = async () => {
    try {
      setBioSaving(true);
      const res = await api.put('/auth/bio', {
        bio: bioInput.trim(),
      });
      const updatedUser = { ...user, bio: res.data.user.bio };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      window.dispatchEvent(new Event('user-updated'));
      toast.success('Bio updated successfully');
    } catch (err: any) {
      console.error('Bio update failed:', err);
      toast.error(err.response?.data?.error || 'Failed to update bio');
    } finally {
      setBioSaving(false);
    }
  };

  const fetchPasskeys = async () => {
    try {
      const res = await api.get('/passkeys/credentials');
      setPasskeys(res.data.credentials || []);
    } catch (err) {
      console.error('Failed to load passkeys:', err);
    }
  };

  const handleRegisterPasskey = async () => {
    try {
      setPasskeyLoading(true);
      const optionsRes = await api.post('/passkeys/register/options');
      const attestation = await startRegistration(optionsRes.data.options);
      await api.post('/passkeys/register/verify', {
        credential: attestation,
        challenge: optionsRes.data.challenge,
        deviceName: passkeyName.trim() || null,
      });
      setPasskeyName('');
      toast.success('Passkey added');
      fetchPasskeys();
    } catch (err: any) {
      console.error('Passkey registration failed:', err);
      toast.error(err.response?.data?.error || 'Failed to add passkey');
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleDeletePasskey = async (credentialId: string) => {
    if (!confirm('Remove this passkey?')) return;
    try {
      await api.delete(`/passkeys/credentials/${credentialId}`);
      toast.success('Passkey removed');
      fetchPasskeys();
    } catch (err: any) {
      console.error('Passkey deletion failed:', err);
      toast.error(err.response?.data?.error || 'Failed to remove passkey');
    }
  };

  const handleSaveGlobalNotifications = async () => {
    if (!globalNotifConfig) return;
    try {
      await api.put('/notifications/global', {
        notifyOnMessage: globalNotifConfig.notifyOnMessage,
        notifyOnMention: globalNotifConfig.notifyOnMention,
      });
      toast.success('Global notification settings saved');
    } catch (err: any) {
      console.error('Failed to save global notification settings:', err);
      toast.error(err.response?.data?.error || 'Failed to save settings');
    }
  };

  const handleApplyToAll = async () => {
    if (
      !confirm(
        'Are you sure you want to overwrite all channel notification settings with your global preferences?',
      )
    ) {
      return;
    }
    try {
      const res = await api.post('/notifications/apply-global');
      toast.success(res.data.message);
    } catch (err: any) {
      console.error('Failed to apply global settings:', err);
      toast.error(err.response?.data?.error || 'Failed to apply settings');
    }
  };

  const [revokingSessions, setRevokingSessions] = useState(false);

  const handleRevokeAllSessions = async () => {
    if (
      !confirm(
        'Are you sure you want to log out of all active devices and sessions? You will be returned to the login screen.',
      )
    ) {
      return;
    }
    setRevokingSessions(true);
    try {
      await api.post('/auth/revoke-all');
      toast.success('All sessions have been revoked. Logging out...');
      setTimeout(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }, 1000);
    } catch (err: any) {
      console.error('Revoke sessions failed:', err);
      toast.error(err.response?.data?.error || 'Failed to revoke sessions');
      setRevokingSessions(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword || deleteConfirmation !== 'DELETE') {
      toast.error('Please enter your password and type DELETE to confirm');
      return;
    }
    if (
      !confirm(
        'Are you absolutely sure? This action cannot be undone and all your data will be permanently deleted.',
      )
    ) {
      return;
    }
    try {
      await api.delete('/invites/account', {
        data: {
          password: deletePassword,
          confirmation: deleteConfirmation,
        },
      });
      toast.success('Account deleted. Logging out...');
      setTimeout(() => {
        localStorage.clear();
        navigate('/login');
      }, 1500);
    } catch (err: any) {
      console.error('Delete account failed:', err);
      toast.error(err.response?.data?.error || 'Failed to delete account');
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="relative bg-brand-surface dark:bg-[#1a1740] rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col z-50 border border-brand-outline-variant/60 dark:border-[#3730a3] overflow-hidden animate-m3-dialog"
      >
        <div className="px-6 py-5 border-b border-brand-outline-variant/60 dark:border-[#3730a3] flex-shrink-0 bg-brand-surface dark:bg-[#1a1740]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-brand-on-surface-variant dark:text-[#c4b5fd]">
                Settings
              </p>
              <h2 className="text-2xl font-bold text-brand-on-background dark:text-[#e0e7ff]">
                User Preferences
              </h2>
            </div>
            <button
              onClick={onClose}
              aria-label="Close user settings"
              className="p-2 rounded-full text-brand-outline hover:text-white hover:bg-white/10 active:scale-95 transition-all text-xl"
            >
              &times;
            </button>
          </div>

          {/* M3 Segmented Tab Bar */}
          <div className="flex gap-1.5 p-1.5 bg-brand-surface-variant/40 dark:bg-black/25 rounded-2xl overflow-x-auto">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-all cursor-pointer whitespace-nowrap active:scale-95 ${
                activeTab === 'profile'
                  ? 'bg-primary-600/30 text-primary-300 font-semibold shadow-sm border border-primary-500/40'
                  : 'text-brand-outline hover:text-white hover:bg-white/10'
              }`}
            >
              👤 Profile
            </button>
            <button
              onClick={() => setActiveTab('voice')}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-all cursor-pointer whitespace-nowrap active:scale-95 ${
                activeTab === 'voice'
                  ? 'bg-primary-600/30 text-primary-300 font-semibold shadow-sm border border-primary-500/40'
                  : 'text-brand-outline hover:text-white hover:bg-white/10'
              }`}
            >
              🎙️ Voice & Audio
            </button>
            <button
              onClick={() => setActiveTab('sounds')}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-all cursor-pointer whitespace-nowrap active:scale-95 ${
                activeTab === 'sounds'
                  ? 'bg-primary-600/30 text-primary-300 font-semibold shadow-sm border border-primary-500/40'
                  : 'text-brand-outline hover:text-white hover:bg-white/10'
              }`}
            >
              🔊 Sounds
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-all cursor-pointer whitespace-nowrap active:scale-95 ${
                activeTab === 'notifications'
                  ? 'bg-primary-600/30 text-primary-300 font-semibold shadow-sm border border-primary-500/40'
                  : 'text-brand-outline hover:text-white hover:bg-white/10'
              }`}
            >
              🔔 Notifications
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-all cursor-pointer whitespace-nowrap active:scale-95 ${
                activeTab === 'security'
                  ? 'bg-primary-600/30 text-primary-300 font-semibold shadow-sm border border-primary-500/40'
                  : 'text-brand-outline hover:text-white hover:bg-white/10'
              }`}
            >
              🔒 Security
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4 flex-1 min-h-0 overflow-y-auto bg-brand-surface dark:bg-[#1a1740]">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <>
              <SectionCard title="Profile" description="Your public avatar and contact details">
                <div className="flex items-center gap-3">
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="avatar"
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-brand-surface-variant" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-brand-on-background dark:text-[#e0e7ff] truncate">
                      {user.username || 'User'}
                    </div>
                    <div className="text-xs text-brand-outline dark:text-[#c4b5fd] truncate">
                      {emailInput || 'No email'}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                      id="avatar-upload"
                      disabled={avatarUploading}
                    />
                    <label
                      htmlFor="avatar-upload"
                      className="px-4 py-2 bg-primary-600 text-brand-on-primary text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-block"
                    >
                      Update Avatar
                    </label>
                    {avatarUploading && (
                      <span className="text-xs text-brand-outline">Uploading…</span>
                    )}
                  </div>
                </div>
              </SectionCard>

              <SectionCard
                title="About Me (Bio)"
                description="Share a short bio with other members on your profile card"
              >
                <div className="space-y-3">
                  <div className="relative">
                    <textarea
                      value={bioInput}
                      onChange={(e) => setBioInput(e.target.value)}
                      maxLength={500}
                      rows={3}
                      placeholder="Write a brief bio about yourself..."
                      className="input w-full resize-none p-3 text-xs sm:text-sm rounded-xl"
                    />
                    <div className="text-right text-[11px] text-brand-outline dark:text-[#c4b5fd] mt-1">
                      {bioInput.length} / 500 characters
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleUpdateBio}
                      disabled={bioSaving || bioInput === (user.bio || '')}
                      className="btn btn-primary px-5 py-2 rounded-xl font-semibold text-xs shadow-md active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {bioSaving ? 'Saving...' : 'Save Bio'}
                    </button>
                  </div>
                </div>
              </SectionCard>

              <div className="grid md:grid-cols-2 gap-4">
                <SectionCard title="Presence" description="Control how you appear to others">
                  <div className="space-y-3">
                    <div className="text-xs text-brand-outline dark:text-[#c4b5fd]">
                      Select your online status:
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: 'online', label: 'Online', icon: '🟢', border: 'border-green-500 bg-green-500/10 text-green-300' },
                        { key: 'idle', label: 'Idle', icon: '🟡', border: 'border-yellow-500 bg-yellow-500/10 text-yellow-300' },
                        { key: 'dnd', label: 'Do Not Disturb', icon: '🔴', border: 'border-red-500 bg-red-500/10 text-red-300' },
                        { key: 'offline', label: 'Invisible', icon: '⚪', border: 'border-gray-500 bg-gray-500/10 text-gray-300' },
                      ].map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => {
                            setStatus(item.key);
                            socketService.emit('user:status-changed', item.key);
                            localStorage.setItem(
                              'user',
                              JSON.stringify({ ...user, status: item.key }),
                            );
                            window.dispatchEvent(new Event('user-updated'));
                          }}
                          className={`px-3 py-2 rounded-lg border text-xs font-medium flex items-center gap-1.5 transition-all ${
                            status === item.key
                              ? `${item.border} ring-2 ring-brand-primary font-bold`
                              : 'border-brand-outline-variant/50 dark:border-[#3730a3] text-brand-on-surface dark:text-[#e0e7ff] hover:bg-brand-surface-variant/30'
                          }`}
                        >
                          <span>{item.icon}</span>
                          <span>{item.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </SectionCard>

                <SectionCard title="Account" description="Username and email">
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      placeholder="New username"
                      className="input w-full"
                    />
                    <input
                      type="password"
                      value={usernamePassword}
                      onChange={(e) => setUsernamePassword(e.target.value)}
                      placeholder="Current password"
                      className="input w-full"
                    />
                    <button
                      onClick={handleUsernameChange}
                      className="btn btn-primary w-full"
                      disabled={!newUsername || !usernamePassword}
                    >
                      Update Username
                    </button>
                  </div>
                  <div className="pt-2 space-y-2 border-t border-brand-outline-variant dark:border-brand-outline">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="Email (blank to remove)"
                      className="input w-full"
                    />
                    <input
                      type="password"
                      value={emailPassword}
                      onChange={(e) => setEmailPassword(e.target.value)}
                      placeholder="Password to confirm"
                      className="input w-full"
                    />
                    <button
                      onClick={handleEmailChange}
                      className="btn btn-secondary w-full"
                      disabled={!emailPassword}
                    >
                      Save Email
                    </button>
                  </div>
                </SectionCard>
              </div>
            </>
          )}

          {/* Voice & Audio Tab */}
          {activeTab === 'voice' && (
            <>
              <SectionCard
                title="🎙️ Voice & Audio"
                description="Configure your microphone, speakers, and voice settings"
              >
                {/* Audio Devices */}
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-brand-primary/30 dark:border-brand-primary/30 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff] mb-3 flex items-center gap-2">
                      <span className="text-lg">🎧</span>
                      Audio Devices
                    </h4>

                    {/* Input Device */}
                    <div className="space-y-2 mb-4">
                      <label className="text-xs font-medium text-brand-on-surface dark:text-brand-on-surface-variant flex items-center justify-between">
                        <span>Microphone</span>
                        <button
                          onClick={refreshDevices}
                          className="text-xs text-brand-primary hover:text-brand-primary dark:text-brand-primary"
                        >
                          🔄 Refresh
                        </button>
                      </label>
                      <select
                        value={selectedInputId || 'default'}
                        onChange={(e) =>
                          setInputDevice(e.target.value === 'default' ? null : e.target.value)
                        }
                        className="input w-full text-sm"
                      >
                        <option value="default">System Default</option>
                        {inputDevices.map((device) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Output Device */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-brand-on-surface dark:text-brand-on-surface-variant">
                        Speakers / Headphones
                      </label>
                      <select
                        value={selectedOutputId || 'default'}
                        onChange={(e) =>
                          setOutputDevice(e.target.value === 'default' ? null : e.target.value)
                        }
                        className="input w-full text-sm"
                      >
                        <option value="default">System Default</option>
                        {outputDevices.map((device) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label}
                          </option>
                        ))}
                      </select>
                      {outputDevices.length === 0 && (
                        <p className="text-xs text-brand-primary-container dark:text-brand-primary flex items-center gap-1">
                          ⚠️ Output device selection not supported in this browser
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Voice Processing */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff] mb-3 flex items-center gap-2">
                      <span className="text-lg">✨</span>
                      Audio Enhancement
                      <span className="ml-auto text-xs font-normal text-brand-outline dark:text-brand-on-surface-variant">
                        (Processed locally)
                      </span>
                    </h4>

                    <div className="space-y-4">
                      {/* Noise Suppression */}
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0 pr-3">
                          <div className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff]">
                            🔇 Noise Suppression
                          </div>
                          <div className="text-xs text-brand-outline dark:text-[#c4b5fd]">
                            Removes background noise like keyboard typing, fans, and traffic
                          </div>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={voicePreferences.voiceNoiseSuppressionEnabled ?? true}
                          disabled={voiceLoading}
                          onClick={() =>
                            updateVoicePreferences({
                              voiceNoiseSuppressionEnabled: !(voicePreferences.voiceNoiseSuppressionEnabled ?? true),
                            })
                          }
                          className={`relative inline-flex h-7 w-12 items-center rounded-full p-1 transition-all cursor-pointer select-none active:scale-95 flex-shrink-0 ${
                            (voicePreferences.voiceNoiseSuppressionEnabled ?? true)
                              ? 'bg-amber-500 shadow-md shadow-amber-500/30'
                              : 'bg-white/15 border border-white/20 hover:bg-white/20'
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                              (voicePreferences.voiceNoiseSuppressionEnabled ?? true) ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Echo Cancellation */}
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0 pr-3">
                          <div className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff]">
                            🔊 Echo Cancellation
                          </div>
                          <div className="text-xs text-brand-outline dark:text-[#c4b5fd]">
                            Prevents audio feedback and echoes from speakers
                          </div>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={voicePreferences.voiceEchoCancellationEnabled ?? true}
                          disabled={voiceLoading}
                          onClick={() =>
                            updateVoicePreferences({
                              voiceEchoCancellationEnabled: !(voicePreferences.voiceEchoCancellationEnabled ?? true),
                            })
                          }
                          className={`relative inline-flex h-7 w-12 items-center rounded-full p-1 transition-all cursor-pointer select-none active:scale-95 flex-shrink-0 ${
                            (voicePreferences.voiceEchoCancellationEnabled ?? true)
                              ? 'bg-amber-500 shadow-md shadow-amber-500/30'
                              : 'bg-white/15 border border-white/20 hover:bg-white/20'
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                              (voicePreferences.voiceEchoCancellationEnabled ?? true) ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Auto Gain Control */}
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0 pr-3">
                          <div className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff]">
                            📊 Auto Gain Control
                          </div>
                          <div className="text-xs text-brand-outline dark:text-[#c4b5fd]">
                            Automatically adjusts your volume to a consistent level
                          </div>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={voicePreferences.voiceAutoGainControlEnabled ?? true}
                          disabled={voiceLoading}
                          onClick={() =>
                            updateVoicePreferences({
                              voiceAutoGainControlEnabled: !(voicePreferences.voiceAutoGainControlEnabled ?? true),
                            })
                          }
                          className={`relative inline-flex h-7 w-12 items-center rounded-full p-1 transition-all cursor-pointer select-none active:scale-95 flex-shrink-0 ${
                            (voicePreferences.voiceAutoGainControlEnabled ?? true)
                              ? 'bg-amber-500 shadow-md shadow-amber-500/30'
                              : 'bg-white/15 border border-white/20 hover:bg-white/20'
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                              (voicePreferences.voiceAutoGainControlEnabled ?? true) ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Voice Activation */}
                  <div className="bg-brand-surface/80 dark:bg-[#252350]/80 border border-brand-outline-variant/60 dark:border-[#3730a3] rounded-2xl p-5 shadow-sm">
                    <h4 className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff] mb-3 flex items-center gap-2">
                      <span className="text-lg">🎤</span>
                      Voice Activation
                    </h4>

                    <div className="space-y-3">
                      {/* Activation Mode */}
                      <div>
                        <label className="text-xs font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-2 block">
                          Broadcast Mode
                        </label>
                        <select
                          value={voicePreferences.voiceActivationMode || 'VOICE_ACTIVITY'}
                          onChange={(e) =>
                            updateVoicePreferences({
                              voiceActivationMode: e.target.value as
                                | 'VOICE_ACTIVITY'
                                | 'PUSH_TO_TALK'
                                | 'ALWAYS_ON',
                            })
                          }
                          disabled={voiceLoading}
                          className="input w-full text-sm"
                        >
                          <option value="VOICE_ACTIVITY">
                            🎙️ Voice Activity (Auto-detect speech)
                          </option>
                          <option value="PUSH_TO_TALK">🔘 Push to Talk (Manual key)</option>
                          <option value="ALWAYS_ON">📡 Always On (Continuous)</option>
                        </select>
                      </div>

                      {/* Sensitivity Slider (only for Voice Activity) */}
                      {(voicePreferences.voiceActivationMode || 'VOICE_ACTIVITY') ===
                        'VOICE_ACTIVITY' && (
                        <div>
                          <label className="text-xs font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-2 flex items-center justify-between">
                            <span>Detection Sensitivity</span>
                            <span className="text-xs text-primary-400 font-bold">
                              {voicePreferences.voiceActivationThreshold ?? -50} dB
                            </span>
                          </label>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-brand-outline">Low</span>
                            <input
                              type="range"
                              min="-100"
                              max="0"
                              step="5"
                              value={voicePreferences.voiceActivationThreshold ?? -50}
                              onChange={(e) =>
                                updateVoicePreferences({
                                  voiceActivationThreshold: parseInt(e.target.value),
                                })
                              }
                              disabled={voiceLoading}
                              className="flex-1 h-2.5 bg-white/20 rounded-full appearance-none cursor-pointer accent-amber-500 hover:accent-amber-400 focus:outline-none transition-all"
                            />
                            <span className="text-xs text-brand-outline">High</span>
                          </div>
                          <p className="text-xs text-brand-outline dark:text-[#c4b5fd] mt-1">
                            Higher sensitivity picks up quieter sounds. Lower reduces false
                            activations.
                          </p>
                        </div>
                      )}

                      {/* Mute on Join */}
                      <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-800">
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={voicePreferences.muteOnJoin ?? false}
                            onChange={(e) =>
                              updateVoicePreferences({ muteOnJoin: e.target.checked })
                            }
                            disabled={voiceLoading}
                            className="mt-1 w-4 h-4 rounded border-brand-outline-variant text-green-500 focus:ring-green-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-brand-on-background dark:text-[#e0e7ff] group-hover:text-green-500 dark:group-hover:text-green-500 transition-colors">
                              🔇 Mute on Join
                            </div>
                            <div className="text-xs text-brand-outline dark:text-[#c4b5fd]">
                              Automatically mute your microphone when joining voice channels
                            </div>
                          </div>
                        </label>
                      </div>

                      {/* Microphone Test Button */}
                      <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-800">
                        <button
                          onClick={() => setIsMonitoringMic(!isMonitoringMic)}
                          className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isMonitoringMic
                              ? 'bg-brand-error hover:bg-brand-error text-brand-on-primary'
                              : 'bg-green-500 hover:bg-green-600 text-brand-on-primary'
                          }`}
                        >
                          {isMonitoringMic ? '🛑 Stop Microphone Test' : '🎤 Test Microphone'}
                        </button>
                        {!permissionGranted && !isMonitoringMic && (
                          <p className="text-xs text-brand-outline dark:text-brand-on-surface-variant mt-2">
                            Click to grant microphone permission and test your audio input
                          </p>
                        )}
                      </div>

                      {/* Voice Activity Meter */}
                      <div
                        className={`mt-4 pt-4 border-t border-green-200 dark:border-green-800 ${isMonitoringMic ? '' : 'opacity-50 pointer-events-none'}`}
                      >
                        <label className="text-xs font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-2 block">
                          Current Voice Activity Level
                        </label>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 bg-brand-surface dark:bg-brand-surface-variant rounded-full h-4 overflow-hidden border border-green-400/30">
                            <div
                              className="h-full bg-gradient-to-r from-green-400 via-emerald-500 to-green-600 transition-all duration-100 shadow-lg shadow-green-500/50"
                              style={{
                                width: `${Math.min(Math.max(voiceActivityLevel * 2, 0), 100)}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-bold text-green-500 dark:text-green-500 w-12 text-right tabular-nums">
                            {Math.round(voiceActivityLevel)}
                          </span>
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                          <div className="flex items-center gap-1 text-brand-outline dark:text-brand-on-surface-variant">
                            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                            Quiet
                          </div>
                          <div className="flex items-center gap-1 text-brand-outline dark:text-brand-on-surface-variant">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            Speaking
                          </div>
                          <div className="flex items-center gap-1 text-brand-outline dark:text-brand-on-surface-variant">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            Loud
                          </div>
                        </div>
                        <p className="text-xs text-brand-outline dark:text-brand-on-surface-variant mt-3">
                          {isMonitoringMic
                            ? 'Speak into your microphone to see the activity level. The meter updates in real-time based on audio input.'
                            : 'Start the microphone test to see activity levels.'}
                        </p>
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="mt-3 bg-brand-surface/50 dark:bg-brand-background/30 rounded-lg p-3 border border-green-200/50 dark:border-green-800/50">
                      <p className="text-xs text-brand-on-surface-variant dark:text-brand-on-surface-variant flex items-start gap-2">
                        <span className="text-sm">💡</span>
                        <span>
                          All voice processing happens on <strong>your device</strong> using your
                          browser's built-in features. The server never processes your audio,
                          keeping it private and reducing server load.
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </SectionCard>
            </>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && globalNotifConfig && (
            <>
              <SectionCard
                title="Global Notification Settings"
                description="Set your default notification preferences for all channels."
              >
                <ToggleSwitch
                  checked={globalNotifConfig.notifyOnMessage}
                  onChange={(v) =>
                    setGlobalNotifConfig({ ...globalNotifConfig, notifyOnMessage: v })
                  }
                  label="Notify on all messages"
                />
                <ToggleSwitch
                  checked={globalNotifConfig.notifyOnMention}
                  onChange={(v) =>
                    setGlobalNotifConfig({ ...globalNotifConfig, notifyOnMention: v })
                  }
                  label="Notify on mentions"
                />
                <div className="pt-2 border-t border-brand-outline-variant dark:border-brand-outline">
                  <button
                    onClick={handleSaveGlobalNotifications}
                    className="btn btn-primary w-full"
                  >
                    Save Global Settings
                  </button>
                </div>
              </SectionCard>

              <SectionCard
                title="Apply to All Channels"
                description="Overwrite all channel-specific notification settings with your global preferences."
              >
                <button onClick={handleApplyToAll} className="btn btn-secondary w-full">
                  Apply Global Settings to All Channels
                </button>
              </SectionCard>
            </>
          )}

          {/* Sounds Tab */}
          {activeTab === 'sounds' && (
            <SoundSettings
              title="Sound Notifications"
              description="Configure audio alerts for messages, mentions, and voice events"
            />
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <>
              <SectionCard title="Password" description="Keep your account secure">
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Current password"
                  className="input w-full"
                />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password (min 8 chars)"
                  className="input w-full"
                />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="input w-full"
                />
                <button
                  onClick={handlePasswordChange}
                  className="btn btn-primary w-full"
                  disabled={!currentPassword || !newPassword || !confirmPassword}
                >
                  Update Password
                </button>
              </SectionCard>

              <SectionCard title="Passkeys" description="Passwordless sign-in">
                <div className="space-y-2">
                  <input
                    type="text"
                    value={passkeyName}
                    onChange={(e) => setPasskeyName(e.target.value)}
                    placeholder="Device name (optional)"
                    className="input w-full"
                  />
                  <button
                    onClick={handleRegisterPasskey}
                    className="btn btn-primary w-full"
                    disabled={passkeyLoading}
                  >
                    {passkeyLoading ? 'Waiting for security key…' : 'Add Passkey'}
                  </button>
                </div>
                <div className="mt-2 space-y-2">
                  {passkeys.length === 0 && (
                    <div className="text-sm text-brand-outline">No passkeys yet.</div>
                  )}
                  {passkeys.map((cred) => (
                    <div
                      key={cred.id}
                      className="flex items-center justify-between bg-brand-background dark:bg-brand-surface rounded px-3 py-2"
                    >
                      <div className="text-sm">
                        <div className="font-medium">{cred.deviceName || 'Unnamed device'}</div>
                        <div className="text-xs text-brand-outline">
                          Added {new Date(cred.createdAt).toLocaleDateString()}
                          {cred.lastUsedAt
                            ? ` · Last used ${new Date(cred.lastUsedAt).toLocaleString()}`
                            : ''}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeletePasskey(cred.id)}
                        className="text-brand-error hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard
                title="Two-Factor Authentication"
                description="Add a second check when signing in"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-brand-on-background dark:text-brand-on-background">
                    Status
                  </span>
                  <span
                    className={`text-xs px-2 py-1 rounded ${twoFAEnabled ? 'bg-green-100 text-green-700' : 'bg-brand-background text-brand-on-surface-variant'}`}
                  >
                    {twoFAEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                {!twoFAEnabled && !setupInProgress && (
                  <button className="btn btn-secondary w-full" onClick={startTwoFASetup}>
                    Enable 2FA
                  </button>
                )}
                {setupInProgress && (
                  <div className="space-y-3">
                    {twoFAQr && <img src={twoFAQr} alt="2FA QR" className="w-40 h-40" />}
                    <input
                      type="text"
                      value={twoFAToken}
                      onChange={(e) => setTwoFAToken(e.target.value)}
                      placeholder="Enter 6-digit code"
                      className="input"
                    />
                    <div className="flex gap-2">
                      <button className="btn btn-primary w-full" onClick={verifyTwoFA}>
                        Verify & Enable
                      </button>
                      <button
                        className="btn btn-secondary w-full"
                        onClick={() => {
                          setSetupInProgress(false);
                          setTwoFASecret('');
                          setTwoFAQr('');
                          setTwoFAToken('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {twoFAEnabled && (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={twoFAToken}
                      onChange={(e) => setTwoFAToken(e.target.value)}
                      placeholder="Enter 6-digit code to disable"
                      className="input"
                    />
                    <button className="btn btn-secondary w-full" onClick={disableTwoFA}>
                      Disable 2FA
                    </button>
                  </div>
                )}
              </SectionCard>

              <SectionCard title="Active Sessions" description="Manage your logged-in devices">
                <div className="space-y-3">
                  <p className="text-xs sm:text-sm text-brand-outline dark:text-[#c4b5fd]">
                    Log out of all devices and active browser sessions. If your account was accessed from an unknown location, this will immediately revoke all tokens and force a sign-in.
                  </p>
                  <button
                    onClick={handleRevokeAllSessions}
                    disabled={revokingSessions}
                    className="btn btn-outlined border-brand-error text-brand-error hover:bg-brand-error hover:text-white w-full transition-all font-semibold cursor-pointer"
                  >
                    {revokingSessions ? 'Revoking Sessions…' : '🔒 Log Out Everywhere (Revoke All Sessions)'}
                  </button>
                </div>
              </SectionCard>

              {!user.isOwner && (
                <SectionCard title="Delete Account" description="This cannot be undone">
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 space-y-3">
                    <p className="text-sm text-brand-on-surface dark:text-brand-on-surface-variant">
                      Permanently remove your account and all data.
                    </p>
                    <input
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder="Enter your password"
                      className="input w-full"
                    />
                    <input
                      type="text"
                      value={deleteConfirmation}
                      onChange={(e) => setDeleteConfirmation(e.target.value)}
                      placeholder="Type DELETE to confirm"
                      className="input w-full"
                    />
                    <button
                      onClick={handleDeleteAccount}
                      disabled={!deletePassword || deleteConfirmation !== 'DELETE'}
                      className="btn bg-brand-error hover:bg-brand-error text-brand-on-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Delete My Account
                    </button>
                  </div>
                </SectionCard>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-brand-outline-variant dark:border-[#3730a3] flex justify-end gap-3 flex-shrink-0 bg-brand-surface dark:bg-[#1a1740]">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-brand-outline-variant dark:border-[#3730a3] rounded-lg text-brand-on-surface dark:text-[#e0e7ff] hover:bg-brand-background dark:hover:bg-[#252350] transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
